import { type ReactNode, useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";
import {
  ACCOUNT_PAGE,
  openDesktopSettings,
  type DesktopSettingsPage,
} from "../../../domains/app/stores/desktopSettingsStore";

/**
 * Where a `/settings/*` address opens the desktop settings modal.
 *
 * Those addresses are in emails, push payloads and older links (the previous
 * desktop workspace published `/settings/general`, `/settings/rooms/leave/:id`
 * and others), so every one of them lands on the page that now holds what it
 * pointed at. Anything unknown opens the modal on Account.
 */
export function desktopSettingsTarget(
  pathname: string,
  search: string
): DesktopSettingsPage {
  const leaving = matchPath<{ roomId: string }>(pathname, {
    path: "/settings/rooms/leave/:roomId",
    exact: true,
  });
  if (leaving !== null)
    return { page: "rooms", leaving: leaving.params.roomId };

  const tab =
    new URLSearchParams(search).get("tab") === "partners"
      ? "partners"
      : "juisci";

  switch (pathname.replace(/\/+$/, "")) {
    case "/settings/edit/profile":
      return { page: "account", editing: "bio" };
    case "/settings/edit/profile/avatar":
    case "/settings/general/avatar":
      return { page: "avatar" };
    case "/settings/account/delete":
      return { page: "deleteAccount" };
    case "/settings/edit/language":
    case "/settings/preferences":
    case "/settings/language":
      return { page: "language" };
    case "/settings/personalization":
    case "/settings/specialty":
      return { page: "specialty" };
    case "/settings/notifications":
      return { page: "notifications" };
    // The button and the partners link of the information email of the
    // partner objection (docs/partner-objection.md, REQ-17).
    case "/settings/partner-sharing":
      return { page: "partnerSharing" };
    case "/settings/partners":
      return { page: "partners" };
    case "/settings/practice":
      return { page: "practice" };
    case "/settings/edit/profile/preferredContent":
    case "/settings/interests":
      return { page: "interests" };
    case "/settings/rooms":
      return { page: "rooms", leaving: null };
    case "/settings/certification":
    case "/settings/request/certification":
      return { page: "certification" };
    case "/settings/terms":
      return { page: "terms", tab };
    case "/settings/privacy":
      return { page: "privacy", tab };
    default:
      return ACCOUNT_PAGE;
  }
}

/**
 * `/settings/*` on desktop web: opens the modal on the page the address names,
 * and sends the reader to their home surface, where the modal sits.
 *
 * The same shape as the mobile routes (`SettingsPage`), which open the settings
 * sheet and redirect: the settings are not a page, and their addresses must
 * keep working.
 */
export function DesktopSettingsRoute({
  home,
}: {
  /** The home redirect AppRoutes computes for this reader. */
  home: ReactNode;
}): ReactNode {
  const { pathname, search } = useLocation();
  useEffect(() => {
    openDesktopSettings(desktopSettingsTarget(pathname, search));
  }, [pathname, search]);
  return home;
}
